import { create } from 'zustand';
import { PANEL_OPEN_DEFAULT, readPanelOpen, writePanelOpen } from '../lib/shellPrefs';

/**
 * The app shell's state machine (M1 · 86d44z1g9).
 *
 * Three pieces of state, and the rules that move them are NOT symmetric — the epic's table is
 * reproduced here because two of the transitions look like typos until you read it:
 *
 *   module rail button   → panelOpen = !alreadyOpenOnThisModule, activeModule = id, notifOpen = false
 *   rail leaf            → navigate, panelOpen = false, notifOpen = false
 *   bell                 → notifOpen = !notifOpen, panelOpen = false
 *   close notif VIA THE PANEL'S OWN control → notifOpen = false, panelOpen = TRUE  (force-opens)
 *   close notif VIA THE BELL                → notifOpen = false, panelOpen stays false
 *   module panel collapse → panelOpen = false
 *   Escape                → closes notifications ONLY
 *
 * ⚠️ "Leaf" is overloaded and the two meanings have opposite rules: a RAIL leaf (Home, What's New,
 * Downloads) navigates AND closes the panel; a PANEL nav row navigates and leaves the panel open.
 * Only the first goes through `pickLeaf`.
 *
 * `activeModule` is DERIVED FROM THE ROUTE. A rail click writes it directly, but that write is a
 * transient override: the next navigation re-resolves it from the pathname (see
 * `useSyncActiveModuleToRoute`). Nothing outside this store should own a second copy — otherwise a
 * programmatic navigation strands the panel on the wrong module.
 *
 * ⚠️ React Compiler: consumers must subscribe with VALUE selectors — `useShellStore((s) => s.panelOpen)`
 * — never by calling a getter in render. See the 86d3d1z40 note in CLAUDE.md.
 */
interface ShellState {
  /** The module panel (M2) is showing. */
  panelOpen: boolean;
  /** The notification panel (M6) is showing. Mutually exclusive with `panelOpen`. */
  notifOpen: boolean;
  /** Id of the module whose panel is (or would be) shown. Route-derived; see above. */
  activeModule: string | null;
  /** Whose preferences `panelOpen` is currently being written against. */
  prefsUserId: number | null;

  /** Rail module button. `alreadyOpen` is `panelOpen && activeModule === id`, computed by the caller. */
  pickModule: (id: string, alreadyOpen: boolean) => void;
  /** RAIL leaf (Home / What's New / Downloads). The NavLink performs the navigation itself. */
  pickLeaf: () => void;
  /** The bell. Also the bell's own close path — closing this way leaves the module panel shut. */
  toggleNotif: () => void;
  /** The notification panel's own close control — this one FORCE-OPENS the module panel behind it. */
  closeNotifRestoringPanel: () => void;
  /** Escape, and any dismissal that must not disturb the module panel. */
  closeNotif: () => void;
  /** The module panel's collapse control. */
  collapsePanel: () => void;
  /** Route → module sync. Ignores `null` so a module-less route (/profile) leaves the panel alone. */
  setActiveModuleFromRoute: (id: string | null) => void;
  /** Load this user's remembered panel state. Called once per signed-in user, from AppLayout. */
  hydratePrefs: (userId: number | null) => void;
}

export const useShellStore = create<ShellState>()((set, get) => {
  /** Apply a panel-open change AND remember it for the current user. */
  const setPanelOpen = (open: boolean, extra: Partial<ShellState> = {}) => {
    writePanelOpen(get().prefsUserId, open);
    set({ panelOpen: open, ...extra });
  };

  return {
    panelOpen: PANEL_OPEN_DEFAULT,
    notifOpen: false,
    activeModule: null,
    prefsUserId: null,

    pickModule: (id, alreadyOpen) =>
      setPanelOpen(!alreadyOpen, { activeModule: id, notifOpen: false }),

    pickLeaf: () => setPanelOpen(false, { notifOpen: false }),

    // Opening notifications hides the module panel, but that is a TRANSIENT displacement, not a
    // preference — so it deliberately does not write through. Otherwise glancing at the bell would
    // permanently collapse the panel for that user.
    toggleNotif: () => set((s) => ({ notifOpen: !s.notifOpen, panelOpen: false })),

    closeNotifRestoringPanel: () => set({ notifOpen: false, panelOpen: true }),

    closeNotif: () => set({ notifOpen: false }),

    collapsePanel: () => setPanelOpen(false),

    setActiveModuleFromRoute: (id) => {
      if (id === null) return;
      set({ activeModule: id });
    },

    hydratePrefs: (userId) => {
      if (get().prefsUserId === userId) return;
      set({ prefsUserId: userId, panelOpen: readPanelOpen(userId) });
    },
  };
});
